import { FONT_HEADING, FONT_TEXT } from '@/features/printables/css/customFonts';
import { hexToHsl } from '@/features/styles/lib/colors';
import {
  EventTextSizes,
  ExperienceColorMode,
  ExperienceVibe,
} from '@/types/gql.generated';
import type { ColorPalette, PaletteNeutralScale } from './color.types';
import { LARGE_EVENT_TEXT_SCALE } from './lib/gridEventText.lib';
import {
  cardTextOn,
  type CategoryFill,
  getAccentLightWash,
  getAccentShadedCell,
  getAccentSolid,
  getColorSet,
  getMutedNeutral,
  getPaletteLookupColor,
  readableInkOn,
  resolveCategoryFill,
  toPaletteColorName,
} from './lib/paletteLookup';
import { applyResponsiveStyles, isResponsiveMatch } from './lib/size.lib';
import type { EventDisplayVariant, Vibe, VibeStyles } from './vibe.types';

// The grid chrome (gridlines + weekend shading) is a tone-matched neutral: a
// warm accent (reds, oranges, yellows, pinks) pairs with the warm-gray ramp; a
// cool accent (greens, blues, purples) with the cool-gray ramp. The tone is
// detected from the accent's own hue rather than the palette slot, so it follows
// whatever primary color the author picks — including custom colors. Near-neutral
// accents (very low saturation, e.g. slate) fall to the cool ramp.
const neutralRampForAccent = (
  palette: ColorPalette,
  accentHex: string
): PaletteNeutralScale => {
  const { hue, saturation } = hexToHsl(accentHex);
  const isWarm = saturation > 8 && (hue < 75 || hue >= 300);
  return isWarm ? palette.colors.warm : palette.colors.cool;
};

// A list row's category bar, minus its fill — see the Planner vibe for why it is its
// own element spanning the row's content height rather than a left border. Hoisted so
// each row's slot call allocates only the fill.
const LIST_ACCENT_BAR = {
  alignSelf: 'stretch',
  borderRadius: '0.1em',
  flexShrink: 0,
  w: '0.2em',
} as const;

// Grid steps off the tone-matched ramp: a hairline gridline and a barely-there
// weekend tint.
const GRID_LINE_STEP = '200';
const GRID_WEEKEND_STEP = '50';

// Day-of-week labels are a quiet neutral in every calendar layout — never the
// accent, which is reserved for the header rule/year and the date numbers. This
// step matches the printable templates' own day-of-week color (#6B6560), so the
// interactive and printable grids read the same.
const DOW_LABEL_STEP = '600';

// The week sheets scale their box header up so the date leads it. The day-of-week
// label sits inside that header, so its em is relative to it — left alone, raising
// the date drags the label up by the same factor and the hierarchy doesn't change.
// The label's value is therefore divided back down, derived from the two below
// rather than hand-computed, so moving WEEK_DATE_EM can't silently resize it.
const WEEK_DATE_EM = 1.3;
const DOW_LABEL_EM = 0.75;
const WEEK_DOW_LABEL_EM = DOW_LABEL_EM / WEEK_DATE_EM;

// A categorized Card paints with the palette's exact fill for that category —
// the same resolver the Style sidebar swatches and the printable cards use — so
// the card matches its swatch and follows the Bold/Light toggle (Bold = the 500
// solid, Light = the 100 wash) along with the readable text pairing that comes
// with it. Returns null for uncategorized cards and every non-card variant,
// which fall back to the tone-matched neutral wash and normal ink.
const cardCategoryFill = (
  variant: EventDisplayVariant | undefined,
  colorName: string | null | undefined,
  paletteId: string | null | undefined,
  colorMode: ExperienceColorMode | null | undefined
): CategoryFill | null =>
  variant === 'card' && colorName
    ? resolveCategoryFill(toPaletteColorName(colorName), paletteId, colorMode)
    : null;

const style: VibeStyles = {
  AccentDisplay: ({ experience, palette }) => {
    return {
      color: getPaletteLookupColor(
        palette,
        experience.primaryColor,
        'shade.500'
      ),
      fontFamily: FONT_HEADING,
      fontStyle: 'italic',
      // CooperBT ships an italic face only at weight 400; pin it so the year
      // uses the real MediumItalic rather than a faux-bolded 700 (the headline
      // wrapper sets 700, which this span would otherwise inherit).
      fontWeight: 400,
      // Guards the italic slant overhang from the header's overflow:hidden clip.
      pr: '0.15em',
    };
  },
  // The weekday abbreviation beside the date in the week box header — a small
  // uppercase label, neutral to match the month grid's day-of-week columns. (The
  // slot name is historical: this text is deliberately not the accent.)
  AccentText: ({ experience, palette, size }) => ({
    color: neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    )[DOW_LABEL_STEP],
    fontFamily: FONT_TEXT,
    // Divided back down inside the week boxes so the label's rendered size holds
    // while the date grows around it (see WEEK_DOW_LABEL_EM). Deliberately
    // isResponsiveMatch and not applyResponsiveStyles: CenterListHeader passes no
    // size, and that helper returns nothing at all for an undefined one.
    fontSize: isResponsiveMatch(size, 'md')
      ? `${WEEK_DOW_LABEL_EM}em`
      : `${DOW_LABEL_EM}em`,
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  }),
  CollectionContainer: () => ({}),
  CollectionGrid: () => ({
    gap: 0,
  }),
  CollectionGridHeader: ({ experience, palette }) => {
    const colors = getColorSet(palette, experience.primaryColor);
    // The accent still drives tone detection (warm-gray ramp for warm accents,
    // cool-gray for cool ones); only the rendered colors are neutral.
    const neutral = neutralRampForAccent(palette, colors.shade['500']);

    return {
      borderBottom: 'solid 1px',
      // The header rule reads as the same gridline tone as the cells below it.
      borderBottomColor: neutral[GRID_LINE_STEP],
      color: neutral[DOW_LABEL_STEP],
      fontFamily: FONT_TEXT,
      fontSize: '0.8em',
      fontWeight: 400,
      textAlign: 'left',
      textTransform: 'uppercase',
    };
  },
  ColorIndicator: ({ experience, palette, category }) => {
    // Only categorized events carry a color; uncategorized fall back to the
    // accent's own neutral ramp (tone-matched to the primary, cool or warm).
    const color = category?.colorName
      ? getPaletteLookupColor(palette, category.colorName, 'shade.500')
      : getPaletteLookupColor(palette, experience.primaryColor, 'neutral.600');

    return {
      color,
      fontSize: '0.55em',
    };
  },
  DayContainer: ({ experience, palette, day, size }) => {
    const hasBlockingEvent = (day?.blockingEvents ?? []).length > 0;
    const isFirstColumn = Boolean(day?.isFirstColumn);
    // Week boxes (size lg) close their own top and bottom, because a week row
    // stands alone: it has no weekday header rule above it to serve as a top edge,
    // and the 5+2 split puts a gutter between its two rows. Month cells take those
    // edges from the header rule and from the row above. Vertical edges are shared
    // in both, so adjacent boxes never stack two 1px rules into a 2px one. Summer
    // keeps the boxes square — the rounded corners the planner vibe uses would
    // soften the crisp ruled look this vibe is going for.
    const closesOwnRow = isResponsiveMatch(size, 'lg');
    const colors = getColorSet(palette, experience.primaryColor);
    // Grid chrome is drawn from the accent's tone-matched neutral ramp so the
    // gridlines and weekend fill pick up the accent's warmth (warm-gray for a
    // warm accent, cool-gray for a cool one) instead of a flat neutral gray.
    const neutral = neutralRampForAccent(palette, colors.shade['500']);
    const borderColor = neutral[GRID_LINE_STEP];

    // The shared grid rules. Every cell — including out-of-bounds ones — draws
    // these, because Summer's month cells inherit their top edge from the row
    // above's bottom border and their left edge from the previous cell's right
    // border (only the first column and the header rule draw their own). Hiding an
    // out-of-bounds cell entirely stripped those, dropping the top rule off the
    // days below the month's leading blank cells and the left rule off the first
    // day beside them.
    const borders = {
      ...(closesOwnRow && {
        borderTop: 'solid 1px',
        borderTopColor: borderColor,
      }),
      borderBottom: 'solid 1px',
      borderBottomColor: borderColor,
      // Only the row's leading cell draws its own left edge; every other cell
      // inherits it from the previous cell's right border, so the two never stack
      // into a 2px rule. Which cell leads is resolved per layout upstream — the
      // week-start day varies per view, and the 5+2 split's weekend row leads on a
      // day that isn't the week start.
      borderLeft: isFirstColumn ? 'solid 1px' : undefined,
      borderLeftColor: isFirstColumn ? borderColor : undefined,
      borderRight: 'solid 1px',
      borderRightColor: borderColor,
    };

    // An out-of-bounds cell (past the month's grid) reads as an empty cell: no
    // date (DayContainerHeader hides it) and no blockout wash, but it keeps the
    // grid rules so the real days below and beside it don't lose their edges.
    //
    // It DOES take the weekend tint, because a leading/trailing blank is still a
    // real weekend day of the neighbouring month: skipping it left a white notch
    // at the top or bottom of a shaded weekend column (an August starting on
    // Saturday broke the Sunday column above the 2nd).
    if (day?.isOutOfBounds) {
      return {
        bg:
          day.isWeekend && experience.shadeWeekendsEnabled
            ? neutral[GRID_WEEKEND_STEP]
            : neutral['0'],
        ...borders,
      };
    }

    return {
      // A blocked-out day is shaded in the accent — design's shaded-cell step, the
      // one fill in this vibe that genuinely wants to read as shading. It has to
      // out-read the weekend tint (the same ramp's 50), which is exactly why that
      // step goes darker than the light wash on the achromatic slots. Where the
      // accent has no visible shade it steps up to the next neutral rather than
      // sharing the weekend step. The weekend tint is gated on the "Shade weekends"
      // toggle.
      bg: hasBlockingEvent
        ? (getAccentShadedCell(palette, experience.primaryColor) ??
          neutral['100'])
        : day?.isWeekend && experience.shadeWeekendsEnabled
          ? neutral[GRID_WEEKEND_STEP]
          : neutral['0'],
      ...borders,
    };
  },
  DayContainerHeader: ({ experience, palette, day, size }) => {
    const colors = getColorSet(palette, experience.primaryColor);

    if (day?.isOutOfBounds) {
      return {
        visibility: 'hidden',
      };
    }

    // Summer's default puts dates in the primary shade; turning "dates in primary
    // color" off falls back to a neutral day number. Both read straight off the
    // injected experience (useVibe resolves parent-first).
    //
    // A muted weekend date comes out of the primary color entirely and takes a
    // fixed step from the neutral ramp this slot is tied to — warm or cool
    // depending on the slot — whenever "Mute weekend dates" is on, regardless of
    // the primary-color setting. Reading from the ramp rather than from the
    // accent's own shades is what keeps professional's white 300 step away from
    // the weekend date, which it used to render invisible. See getMutedNeutral
    // for why the step no longer varies with the accent.
    const muteWeekend = Boolean(
      day?.isWeekend && experience.muteWeekendDatesEnabled
    );
    const color = muteWeekend
      ? getMutedNeutral(colors.neutral)
      : experience.datesInPrimaryColor
        ? colors.shade['500']
        : colors.neutral['700'];

    return applyResponsiveStyles(size, {
      base: {
        color,
        fontFamily: FONT_HEADING,
        fontSize: '1em',
        fontWeight: 600,
        textAlign: 'left',
      },
      // The week sheets (size md) give the date a step up so it leads its box
      // header instead of sitting level with the weekday beside it, which steps
      // down to compensate (see WEEK_DATE_EM). The month grid stays at base: its
      // cells are far smaller and carry only a day number, no label to out-read.
      md: {
        fontSize: `${WEEK_DATE_EM}em`,
      },
    });
  },
  EventCardContainer: ({
    experience,
    palette,
    isInteractive,
    eventTextSize,
  }) => {
    return {
      color: getPaletteLookupColor(
        palette,
        experience.primaryColor,
        'neutral.700'
      ),
      // 'inherit' (not undefined) so it survives the interactive button reset
      // spread, which sets fontSize:'inherit'.
      fontSize:
        eventTextSize === EventTextSizes.Large
          ? `${LARGE_EVENT_TEXT_SCALE}em`
          : 'inherit',
      letterSpacing: '-0.008em',
      lineHeight: '1.25',
      _hover: isInteractive
        ? {
            borderRadius: 'md',
            transition:
              'box-shadow 0.15s ease, opacity 0.15s ease, transform 0.15s ease',
            _hover: {
              boxShadow: 'md',
              opacity: 0.8,
              transform: 'translateY(-1px)',
            },
          }
        : {},
    };
  },
  // Card display type: a solid category-tinted fill, no border. Uncategorized
  // events fall back to the accent's own neutral ramp (tone-matched to the
  // primary) rather than its shade, so an uncategorized card reads as a soft
  // neutral rather than looking categorized. Returns nothing for other variants
  // so the layout can spread it unconditionally.
  EventCardSurface: ({
    experience,
    palette,
    category,
    variant,
    categoryColorsEnabled = true,
  }) => {
    if (variant !== 'card') return {};
    const neutral = neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    );
    const geometry = { borderRadius: '0.5em', p: '0.4em 0.55em' } as const;

    // Color categories off: the whole view is deliberately uncolored, so a filled
    // panel would only add tonal noise. Outline the card on white instead — it
    // still reads as a card against the cell, without implying a category.
    if (!categoryColorsEnabled) {
      return {
        ...geometry,
        bg: neutral['0'],
        border: 'solid 1px',
        borderColor: neutral['300'],
      };
    }

    const fill = cardCategoryFill(
      variant,
      category?.colorName,
      experience.colorPalette,
      experience.colorMode
    );

    return {
      ...geometry,
      // With colors on, an uncategorized card sits among tinted siblings, so it
      // reads as a soft neutral panel — one step down from the lightest neutral —
      // rather than the outline used when nothing on the view is colored.
      bg: fill?.background ?? neutral['100'],
    };
  },
  // Line display type: a left accent bar in the category color (accent-neutral
  // when uncategorized).
  EventAccentBar: ({ experience, palette, category, variant }) => {
    if (variant !== 'line') return {};
    return {
      borderLeft: '0.2em solid',
      borderLeftColor: category?.colorName
        ? getPaletteLookupColor(palette, category.colorName, 'shade.500')
        : getPaletteLookupColor(
            palette,
            experience.primaryColor,
            'neutral.500'
          ),
      pl: '0.55em',
    };
  },
  // Text display type: the title carries the category hue (accent-neutral ink
  // when uncategorized). Normal weight — the color, not the weight, distinguishes
  // it — at the inherited body size to match the printable look.
  // The title is the primary text in every display type, so it takes the darkest
  // step of the tone-matched ramp and the timing beside it stays a lighter
  // neutral. Weight is left to the layout except where noted.
  EventTitle: ({ experience, palette, category, variant }) => {
    const ink = getPaletteLookupColor(
      palette,
      experience.primaryColor,
      'neutral.900'
    );

    const categoryHue = category?.colorName
      ? getPaletteLookupColor(palette, category.colorName, 'shade.500')
      : null;

    // Card: the ink, unless a categorized fill supplies its own readable pairing
    // (white on a Bold 500, ink on a Light wash).
    if (variant === 'card') {
      const fill = cardCategoryFill(
        variant,
        category?.colorName,
        experience.colorPalette,
        experience.colorMode
      );

      return { color: fill?.text ?? ink };
    }

    // The other variants sit straight on the day cell.
    const cell = palette.colors.warm['0'];

    // Text: the title itself carries the category hue, since it's the only
    // category signal this variant has. Normal weight — the color, not the
    // weight, distinguishes it — at the inherited body size.
    if (variant === 'text') {
      return {
        color: categoryHue ? readableInkOn(categoryHue, cell) : ink,
        fontWeight: 400,
      };
    }

    // Line and bullet: the hue lives in the bar / marker, so tinting the title too
    // would double the signal.
    if (variant === 'line' || variant === 'bullet') {
      return { color: ink };
    }

    return {};
  },
  EventLocationText: ({ experience, palette, category, variant }) => {
    // On a categorized Card the surface may be a solid Bold fill, where the
    // neutral ramp would be illegible — ride the fill's text pairing instead,
    // held back slightly so location stays secondary to the title.
    const fill = cardCategoryFill(
      variant,
      category?.colorName,
      experience.colorPalette,
      experience.colorMode
    );

    return {
      // The time's rung, not a lighter one. 500 read 3.00:1 on a plain cell, and this
      // vibe washes a blocked day in the *accent's* shaded step rather than a neutral,
      // where even 600 fell to 3.27:1 (sunset's gray slot, a deliberately dark
      // #C1C1C4). 700 clears 4.5:1 on every fill and slot but that one. Matching the
      // time is also the honest hierarchy — they are one metadata group, sharing a line
      // on the interactive week.
      color:
        fill?.text ??
        getPaletteLookupColor(palette, experience.primaryColor, 'neutral.700'),
      fontSize: '0.9em',
      ...(fill && { opacity: 0.85 }),
    };
  },
  EventTimeText: ({ experience, palette, category, variant }) => ({
    // Outside a categorized Card the time always reads as neutral text (the
    // accent's own tone-matched ramp) regardless of category — the category
    // color lives in the title, not the time. On a Card it follows the fill's
    // readable pairing so a solid Bold surface stays legible.
    color:
      cardCategoryFill(
        variant,
        category?.colorName,
        experience.colorPalette,
        experience.colorMode
      )?.text ??
      getPaletteLookupColor(palette, experience.primaryColor, 'neutral.700'),
    fontWeight: 500,
  }),
  // Printable List section header (e.g. "April 2026"): the Cooper display face
  // in the accent shade, mixed case (not uppercase like the planner-grid header),
  // with an accent-colored underline rule.
  ListGroupHeader: ({ experience, palette }) => {
    const colors = getColorSet(palette, experience.primaryColor);
    const neutral = neutralRampForAccent(palette, colors.shade['500']);

    return {
      borderBottom: 'solid 1px',
      // The tone-matched gridline step, not the accent's own 300: that step is a
      // pale wash on several slots (joyful's yellow 300 is #FBE193), which barely
      // reads as a rule at all. Now that rows draw no divider this is the only
      // rule on the page, so it has to be both visible and in the same tonal
      // family as everything around it.
      borderBottomColor: neutral['300'],
      color: colors.shade['500'],
      fontFamily: FONT_HEADING,
      fontSize: '1.3em',
      fontWeight: 500,
      letterSpacing: '-0.01em',
      textTransform: 'none',
    };
  },
  // Printable List row date ("Apr 4"): the Cooper display face in the accent
  // shade when the view opts in, otherwise on the same neutral ramp as the row.
  ListEventDate: ({ experience, palette }) => {
    const colors = getColorSet(palette, experience.primaryColor);
    const neutral = neutralRampForAccent(palette, colors.shade['500']);

    return {
      color: experience.datesInPrimaryColor
        ? colors.shade['500']
        : neutral['700'],
      fontFamily: FONT_HEADING,
      fontSize: '1.05em',
      fontWeight: 500,
      letterSpacing: '-0.01em',
      textTransform: 'none',
    };
  },
  // The ink a list row's secondary text (location and time) inherits, off the
  // accent's tone-matched ramp — the row previously drew from the app design
  // system's flat grays, which are tonally unrelated to the rest of the sheet. The
  // description sets its own, one step lighter, via ListEventDescription.
  ListRow: ({ experience, palette }) => ({
    color: neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    )['700'],
  }),
  // Full-strength 500, as this vibe's grid accent bar carries it. Wider than
  // Planner's hairline, in keeping with a vibe whose grid draws solid rules.
  ListRowAccentBar: ({ palette, category, categoryColorsEnabled = true }) => {
    if (!categoryColorsEnabled || !category?.colorName) return {};

    return {
      ...LIST_ACCENT_BAR,
      bg: getPaletteLookupColor(palette, category.colorName, 'shade.500'),
    };
  },
  // The row's primary text, darkened off the same ramp so the title leads the
  // secondary lines it sits above.
  ListEventTitle: ({ experience, palette }) => ({
    color: neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    )['900'],
  }),
  // The row's time takes the vibe's display face, pairing it with the date badge
  // across the row. Color is left to the row's inherited secondary ink so the time
  // stays quieter than the title.
  ListEventTime: () => ({
    fontFamily: FONT_HEADING,
  }),
  // One step lighter on the same tone-matched ramp than the row's secondary ink,
  // so a long description settles behind the title and location.
  ListEventDescription: ({ experience, palette }) => ({
    color: neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    )['500'],
  }),
  // The weekday micro-label under the date, on the same step this vibe gives
  // day-of-week labels in every calendar layout — so the printable list's weekday
  // reads at the same weight as the grids' column headers.
  ListEventWeekday: ({ experience, palette }) => ({
    color: neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    )[DOW_LABEL_STEP],
  }),
  // The sheet's headline takes the Cooper display face, matching the group headers
  // beneath it so the page reads as one type family — but not their accent colour.
  // The accent is spent on the group rules and the row dates, which repeat down the
  // page; the title is a single piece of branding at the top and stays neutral ink,
  // the same darkest step ListEventTitle uses. (It briefly took shade.500 when the
  // slot was split off PageHeaderTitle, which had always inked it neutral.)
  ListHeaderTitle: ({ experience, palette }) => ({
    color: neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    )['900'],
    fontFamily: FONT_HEADING,
    fontWeight: 500,
    letterSpacing: '-0.01em',
    textTransform: 'none',
  }),
  // The date range is a subtitle: the body face on the accent's tone-matched
  // neutral ramp, so it supports the title rather than repeating its face.
  ListHeaderDateRange: ({ experience, palette }) => ({
    color: neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    )['600'],
    fontFamily: FONT_TEXT,
    fontWeight: 400,
    letterSpacing: 'normal',
    textTransform: 'none',
  }),
  // Keeps the rule, on the accent — matching the group rules and row dates below it.
  // Resolving the accent here is also what stops it printing black: the graphic's
  // var(--experience-primary-color) fallback does not resolve in the print iframe.
  ListHeaderRule: ({ experience, palette }) => ({
    color: getPaletteLookupColor(palette, experience.primaryColor, 'shade.500'),
  }),
  Legend: ({ experience, palette, isMobile, isPrintEnv }) => ({
    color: getPaletteLookupColor(
      palette,
      experience.primaryColor,
      'neutral.600'
    ),
    fontFamily: FONT_TEXT,
    fontSize: isMobile && !isPrintEnv ? '0.85em' : '0.75em',
    letterSpacing: '0.025em',
  }),
  // A blocked-day swatch: its fill and outline. Must agree with DayContainer's
  // blocked-day branch — the accent's shaded-cell step with the same
  // neutral['100'] fallback, outlined in the tone-matched gridline step the cells
  // rule themselves with. This vibe washes a blocked day in the accent, so a gray
  // swatch would advertise a color the grid never paints.
  LegendBlockIndicator: ({ experience, palette }) => {
    const neutral = neutralRampForAccent(
      palette,
      getColorSet(palette, experience.primaryColor).shade['500']
    );

    return {
      color:
        getAccentShadedCell(palette, experience.primaryColor) ?? neutral['100'],
      borderColor: neutral[GRID_LINE_STEP],
    };
  },
  // The list card is lifted with the same shadow the interactive month/week
  // grid card uses. Overrides the boldness-driven card shadow.
  ListCardSurface: () => ({
    boxShadow: 'ah-card-raised',
  }),
  // The date is the card's one accent-colored line when the view opts in;
  // otherwise it returns to the card's neutral metadata ink.
  ListCardDate: ({ experience, palette }) => {
    const colors = getColorSet(palette, experience.primaryColor);
    const neutral = neutralRampForAccent(palette, colors.shade['500']);

    return {
      color: experience.datesInPrimaryColor
        ? colors.shade['500']
        : neutral['700'],
    };
  },
  ListDateBadge: ({ experience, palette }) => {
    const colors = getColorSet(palette, experience.primaryColor);
    const neutral = neutralRampForAccent(palette, colors.shade['500']);

    return {
      color: experience.datesInPrimaryColor
        ? colors.shade['500']
        : neutral['900'],
    };
  },
  // A month box reads as a card on the page: white surface, hairline in the
  // accent's tone-matched gridline step (the same rule the month grid draws), and
  // the softly rounded corner the printable cards use. Summer keeps its *day*
  // boxes square, but a month card is a card — the radius is what separates it
  // from the ruled grid.
  MonthCardContainer: ({ experience, palette }) => {
    const colors = getColorSet(palette, experience.primaryColor);
    const neutral = neutralRampForAccent(palette, colors.shade['500']);

    return {
      bg: neutral['0'],
      border: 'solid 1px',
      borderColor: neutral[GRID_LINE_STEP],
      borderRadius: '0.6em',
    };
  },
  // The label band is the month card's one accent-bearing element: the accent's light
  // wash rather than a solid fill, so the event text below stays the focus. The wash
  // (shade 100), not the shaded-cell 50 — the band is a tinted surface, not shading,
  // and 50 runs darker than the wash on the achromatic slots.
  // The band carries the accent; the month name itself is neutral ink. Putting the
  // accent's own 500 shade on that wash is same-hue-on-same-hue and fails badly
  // at the light end of the palette (a yellow accent lands at 1.4:1), so the
  // legible pairing is the tone-matched ink — the accent still reads, via the fill.
  // Cooper, mixed case, matching the header's month headline rather than the
  // uppercase grid labels.
  // The band follows the Bold/Light toggle the way a Card's surface does: Light is the
  // accent's 100 wash above, Bold its 500 solid — the same two steps
  // resolveCategoryFill picks between, so a Bold month card and a Bold event card read
  // as one decision. Bold takes cardTextOn rather than the wash's tone-matched ink,
  // which is illegible on a 500 solid; the luma pick is what keeps a pale accent
  // (cloud's yellow) on dark ink while a saturated one goes white. Bold drops the
  // hairline too, a 200 rule reading as a seam against the solid.
  MonthCardHeader: ({ experience, palette }) => {
    const colors = getColorSet(palette, experience.primaryColor);
    const neutral = neutralRampForAccent(palette, colors.shade['500']);
    const type = {
      fontFamily: FONT_HEADING,
      fontSize: '1.05em',
      fontWeight: 500,
      letterSpacing: '-0.01em',
      textTransform: 'none',
    } as const;

    if (experience.colorMode === ExperienceColorMode.Bold) {
      // An effectively-white accent has no solid to paint, so it falls to the
      // tone-matched neutral's dark end — the same substitution the wash makes below,
      // at Bold's strength.
      const solid =
        getAccentSolid(palette, experience.primaryColor) ?? neutral['800'];

      return { ...type, bg: solid, color: cardTextOn(solid) };
    }

    return {
      ...type,
      // The band is the card's one accent-bearing element, so it needs a fill that
      // actually shows — a white wash would leave the month name floating with no
      // band at all. Falls back to the tone-matched neutral in that case.
      bg:
        getAccentLightWash(palette, experience.primaryColor) ?? neutral['100'],
      borderBottom: 'solid 1px',
      borderBottomColor: neutral[GRID_LINE_STEP],
      color: neutral['900'],
    };
  },
  // The date leading each event row: Cooper in the accent, so the dates carry the
  // same treatment as the day numbers in the month grid. Follows the same
  // "dates in primary color" switch, falling back to neutral ink when it's off.
  // Gentler than the grid's 1.22em: a month-card box is one of three columns
  // holding a dense list of dated rows, so the grid's step overflows it. Tunable.
  MonthCardEventContainer: ({ eventTextSize }) => ({
    fontSize: eventTextSize === EventTextSizes.Large ? '1.1em' : 'inherit',
  }),
  MonthCardEventDate: ({ experience, palette }) => {
    const colors = getColorSet(palette, experience.primaryColor);

    return {
      color: experience.datesInPrimaryColor
        ? colors.shade['500']
        : colors.neutral['700'],
      fontFamily: FONT_HEADING,
      fontWeight: 500,
    };
  },
  // The row's trailing details (weekday, location, time): the accent's tone-matched
  // neutral, a step lighter than the date so they stay secondary to the title.
  MonthCardEventSecondary: ({ experience, palette }) => ({
    color: getColorSet(palette, experience.primaryColor).neutral['500'],
  }),
  // Content, not metadata: darkens to the same 900 step the title takes rather than
  // staying on the secondary tone, where it was indistinguishable from the location
  // beside it. Never the category hue — the title already carries that.
  MonthCardEventDescription: ({ experience, palette }) => ({
    color: getColorSet(palette, experience.primaryColor).neutral['900'],
  }),
  // The title leads the row, so it darkens to the 900 step of the same ramp the
  // secondary text draws from — one tonal family, two levels of emphasis.
  // Month cards carry the category on the title, the way the Text display type
  // does on a grid — the row has no card or marker to put it on. Darkened where
  // the hue would not read as ink on the card's white box.
  MonthCardEventTitle: ({ experience, palette, category }) => ({
    color: category?.colorName
      ? readableInkOn(
          getPaletteLookupColor(palette, category.colorName, 'shade.500'),
          palette.colors.warm['0']
        )
      : getColorSet(palette, experience.primaryColor).neutral['900'],
  }),
  // The page content itself stays white; the tint lives on PageCanvas behind it.
  Page: ({ experience, palette }) => ({
    bg: palette.colors.warm['0'],
    color: getPaletteLookupColor(
      palette,
      experience.primaryColor,
      'neutral.900'
    ),
  }),
  PageHeaderContainer: ({ palette, experience }) => {
    const color = getPaletteLookupColor(
      palette,
      experience.primaryColor,
      'shade.500'
    );

    return {
      // Overrides the header component's default flex-end so the two titles center
      // against each other. Unconditional — the header wraps on content, so no
      // viewport reliably means "stacked".
      alignItems: 'center',
      borderBottomStyle: 'solid',
      borderBottomWidth: '0.125em',
      borderColor: color,
      // Breathing room above the grid's accent rule, kept tight: the header sits on
      // a snug line box, so a deeper pad reads as a gap. A wrapped header takes its
      // room from the component's rowGap instead.
      pb: '0.25em',
      _before: {
        bg: color,
        borderRadius: '50%',
        bottom: 0,
        content: '" "',
        h: '0.5em',
        left: 0,
        position: 'absolute',
        transform: 'translate(-50%, calc(50% + 0.0625em))',
        w: '0.5em',
      },
    };
  },
  // The planner title, rendered on the right of the header as a branding mark
  // (the header component places it there for Summer): the Cooper display face
  // in dark neutral ink, mixed case.
  PageHeaderTitle: ({ experience, palette }) => ({
    color: getPaletteLookupColor(
      palette,
      experience.primaryColor,
      'neutral.900'
    ),
    fontFamily: FONT_HEADING,
    fontSize: '1.25em',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    lineHeight: '1',
    textOverflow: 'ellipsis',
  }),
  PageHeaderSecondary: ({ isPrintEnv }) => {
    return {
      fontFamily: FONT_HEADING,
      fontSize: isPrintEnv ? '2em' : { base: '1.5em', md: '2em' },
      fontWeight: 700,
    };
  },
  PageNumberPrimary: () => ({
    fontFamily: FONT_HEADING,
    fontSize: '1.25em',
    fontWeight: 400,
  }),
};

export const summerVibe: Vibe = {
  id: ExperienceVibe.Summer,
  meta: {
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
    fontWeight: 400,
    name: 'Storybook',
  },
  supportedDisplaySettings: {
    accentColor: true,
    datesInPrimaryColor: true,
    highlightToday: true,
  },
  style,
};
